import { Divider, Stack } from '@mui/material';

import { Box, HelpTip, Typography } from '@occc-app/design-system';
import { Flow_Types } from '@occc-app/offerHelpers/types';
import { AwardCount } from '@occc-app/routes/CampaignComponents/components/AwardCount';
import { CustomData } from '@occc-app/routes/CampaignComponents/components/CustomData/CustomData';
import { CampaignComponentFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentFooterActions';
import { CampaignComponentReadOnlyFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentReadOnlyFooterActions';
import { OfferEarningMechanismConfigurationSection } from '@occc-app/offerHelpers/components/offer-earning-mechanism-configuration.section';
import { OfferConfigurationSection } from '@occc-app/offerHelpers/components/offer-configuration-section';
import { PromptAtPump } from '@occc-app/routes/CampaignComponents/PromptAtPump/PromptAtPump';
import OfferTypeSelectField from '@occc-app/routes/CampaignComponents/components/OfferTypeSelect';
import { useConfig } from '@occc/config';

import {
  FulfillmentChannelConstraint,
  FulfillmentWindow,
  PaymentMethodConstraint,
  Stackable,
  VoucherStacking,
} from '@occc/feat-constraints';
import {
  useIsFuelRollbackVoucherStackingEnabled,
  useRollbackStackingConfig,
} from '@occc-app/Chain/hooks';
import { OfferConfigurationWizardFormLayout } from '@occc-app/routes/CampaignComponents/components/offer-configuration-wizard-form-layout';

import { OfferNameAndDescriptionConfiguration } from '@occc-app/routes/Offer/components/OfferNameAndDescriptionConfiguration';
import { TransactionDiscountFormContent } from './EarningMechanismComponents/TransactionDiscountFormContent';
import { ProductDiscountFormContent } from './EarningMechanismComponents/ProductDiscountFormContent';
import { SweepstakesFormContent } from './EarningMechanismComponents/SweepstakesFormContent';
import { FuelRollbackFormContent } from './EarningMechanismComponents/FuelRollbackFormContent';
import { ExternalFulfillmentFormContent } from './EarningMechanismComponents/ExternalFulfillmentFormContent';
import { AllowCarryOverAdvancedSetting } from './AllowCarryOverAdvancedSetting';
import { offerTypes } from './constants';

import {
  getIsTransactionFrequencyLimitSupportedForAward,
  getIsTransactionLimitSupported,
} from './utils';
import { AWARD_TYPES, ConfigureFormRenderProps, PRODUCT_DISCOUNT_TYPE } from '../../types';

type ConfigureFormContentProps = Pick<ConfigureFormRenderProps, 'values'> & {
  setAwardType: (type: AWARD_TYPES) => void;
  isReadOnlyRole: boolean;
  currentFlowType: Flow_Types;
  submitting?: boolean;
  dirty?: boolean;
  handleNextButtonClick?: () => void;
  handleCancel?: () => void;
};

export const ConfigureFormContent = ({
  values,
  setAwardType,
  isReadOnlyRole,
  currentFlowType,
  handleNextButtonClick,
  handleCancel,
  submitting,
  dirty,
}: ConfigureFormContentProps) => {
  const formFooterActionButtons = isReadOnlyRole ? (
    <CampaignComponentReadOnlyFooterActions
      onCancel={handleCancel}
      onNextStepClick={handleNextButtonClick}
    />
  ) : (
    <CampaignComponentFooterActions
      currentFlowType={currentFlowType}
      onCancel={handleCancel}
      isSubmitDisabled={submitting || (currentFlowType === Flow_Types.Update && !dirty)}
    />
  );

  const rollbackStackingConfig = useRollbackStackingConfig();
  const isFuelRollbackVoucherStackingEnabled = useIsFuelRollbackVoucherStackingEnabled();
  const { config } = useConfig();
  const isOrderAheadEnabled = useConfig('orderAheadConfig').enabled;

  const canShowCustomData =
    values.awardType &&
    [
      AWARD_TYPES.TRANSACTION,
      AWARD_TYPES.PRODUCT,
      AWARD_TYPES.SWEEPSTAKES,
      AWARD_TYPES.FUEL_ROLLBACK,
    ].includes(values.awardType);

  const canShowHelpTip =
    values.awardType && [AWARD_TYPES.TRANSACTION, AWARD_TYPES.PRODUCT].includes(values.awardType);

  const canShowAllowCarryOver =
    values.awardType === AWARD_TYPES.PRODUCT &&
    values.productDiscount?.type === PRODUCT_DISCOUNT_TYPE.DISCOUNTED;

  const canShowOfferAdvancedSettings = canShowCustomData || canShowHelpTip || canShowAllowCarryOver;

  return (
    <>
      <OfferEarningMechanismConfigurationSection>
        <OfferTypeSelectField
          items={offerTypes}
          name='awardType'
          onChange={setAwardType}
          isChangeButtonVisible={!isReadOnlyRole}
        />

        {values.awardType && (
          <Stack>
            <Box mb={2}>
              <OfferNameAndDescriptionConfiguration entityName='Award' />
            </Box>

            {values.awardType === AWARD_TYPES.TRANSACTION && (
              <TransactionDiscountFormContent values={values} />
            )}
            {values.awardType === AWARD_TYPES.PRODUCT && (
              <ProductDiscountFormContent values={values} />
            )}
            {values.awardType === AWARD_TYPES.SWEEPSTAKES && <SweepstakesFormContent />}
            {values.awardType === AWARD_TYPES.FUEL_ROLLBACK && <FuelRollbackFormContent />}
            {values.awardType === AWARD_TYPES.EXTERNAL_FULFILLMENT && (
              <ExternalFulfillmentFormContent values={values} />
            )}
          </Stack>
        )}
      </OfferEarningMechanismConfigurationSection>

      {values.awardType && (
        <OfferConfigurationWizardFormLayout actions={formFooterActionButtons}>
          {[AWARD_TYPES.TRANSACTION, AWARD_TYPES.PRODUCT, AWARD_TYPES.FUEL_ROLLBACK].includes(
            values.awardType,
          ) && (
            <OfferConfigurationSection title='Limits'>
              <AwardCount
                values={values}
                shouldShowTransactionLimit={getIsTransactionLimitSupported(values.awardType)}
                shouldShowTransactionFrequencyLimit={getIsTransactionFrequencyLimitSupportedForAward(
                  values.awardType,
                )}
                description={
                  values.awardType === AWARD_TYPES.FUEL_ROLLBACK &&
                  values.voucherStackingConstraint.breakageRefund
                    ? 'Limits the number of fulfillments. Note: "Per Transaction Fulfillment Limit" is required when "Refund eligible" enabled'
                    : undefined
                }
              />
            </OfferConfigurationSection>
          )}

          {[AWARD_TYPES.TRANSACTION, AWARD_TYPES.PRODUCT, AWARD_TYPES.FUEL_ROLLBACK].includes(
            values.awardType,
          ) && (
            <OfferConfigurationSection title='Requirements'>
              {values.awardType === AWARD_TYPES.FUEL_ROLLBACK &&
                config.features.promptAtPumpEnabled && <PromptAtPump values={values} />}

              {values.awardType === AWARD_TYPES.FUEL_ROLLBACK && (
                <PaymentMethodConstraint values={values} />
              )}

              <FulfillmentWindow values={values} />

              {isOrderAheadEnabled && (
                <FulfillmentChannelConstraint values={values.fulfillmentChannelConstraint} />
              )}

              {values.awardType === AWARD_TYPES.FUEL_ROLLBACK && (
                <>
                  <Stackable disabled={['OFF', 'ALL'].includes(rollbackStackingConfig)} />
                  <Divider />
                  <VoucherStacking disabled={!isFuelRollbackVoucherStackingEnabled} />
                </>
              )}
            </OfferConfigurationSection>
          )}

          {canShowOfferAdvancedSettings ? (
            <OfferConfigurationSection title='Advanced Offer Settings' mb={0}>
              {canShowAllowCarryOver && <AllowCarryOverAdvancedSetting disabled={isReadOnlyRole} />}

              {canShowCustomData && <CustomData values={values.customData} />}

              {canShowHelpTip && (
                <Box p={3}>
                  <HelpTip title='Promotional discount fulfillments'>
                    <Typography variant='body2' color='textSecondary'>
                      Promotional discount fulfillments are incremented upon the successful
                      finalization of a loyalty transaction, as such they may have a slight risk of
                      over-running if multiple customers are transacting simultaneously as the limit
                      is reached.
                    </Typography>
                  </HelpTip>
                </Box>
              )}
            </OfferConfigurationSection>
          ) : null}
        </OfferConfigurationWizardFormLayout>
      )}
    </>
  );
};
